<template>
  <div class="room-page">
    <template v-if="isRoomError"><error-404/></template>
    <div class="room" v-else-if="roomInfo">
      <div class="room-content" ref="roomContentEl">
        <room-info :roomId="roomId" :roomInfo="roomInfo" />
          
          <div class="room-video-container">
            <room-video v-if="roomInfo.state===1" :roomInfo="roomInfo" :autoSwitchQuality="true"/>
            <room-video-closed  v-if="roomInfo.state===0" :roomInfo="roomInfo"/>
          </div>
         
        <div class="gift-control-container" v-if="showGifts">
          <gift-control />
        </div>
        <payment-dialog />

        <div class="room-bot-banner">
          <img src="~@/assets/bot-banner.jpg" class="room-bot-banner-img" />
        </div>
      </div>

      <div class="comment-area">
        <div class="live-rank-container" v-if="showRank && isBrowser">
          <rank-list :onLogin="onLogin" />
        </div>
        <div class="live-comment-container" :style="commentContainerStyle">
          <animation-control/>
          <live-comment :onLogin="onLogin" :showHeader="!showRank" v-if="isBrowser" />
          <div class="comment-area-loading" v-else>
            <img src="@/assets/rank-list/loading.png"  class="comment-area-loading__img"/>
            <p class="comment-area-loading__txt">Loading</p>
          </div>
        </div>
        <div class="gift-effect-container">
          <gift-effect/>
        </div>
        <div class="live-introduction-container">
          <room-introduction :roomInfo="roomInfo" />
        </div>
      </div>
    </div> 
    
    <admin-modal/>
    <mute-modal/>
    <report-modal/>
    <warning-modal/>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, onBeforeUnmount, nextTick, provide } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useLogin } from '@bilibili-intl/login';
import Error404 from '@/components/error/404.vue';
import LiveComment from '@/components/comment';
import AnimationControl from '@/components/animations';
import RoomVideo from '@/components/room-video';
import RoomVideoClosed from '@/components/room-video-closed';
import PaymentDialog from './components/payment';
import modals from '@/components/modals';
import { useServerPrefetch } from '@/composables/use-server-prefetch';
import { useHostingEnv } from '@/common/env';
import { initBSocket, closeBSocket } from '@/service/broadcast';
import { DATA_ERROR } from '@/constants';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import RankList from './components/rank-list.vue';
import RoomIntroduction from './components/room-introduction.vue';
import RoomInfo from './components/room-info.vue';
import GiftControl from './components/gift-control/gift-control.vue';
import GiftEffect from '@/components/animations/gift/gift-effect.vue';


export default defineComponent({
  components: {
    RoomInfo,
    RoomVideo,
    RoomVideoClosed,
    GiftControl,
    LiveComment,
    AnimationControl,
    RankList,
    RoomIntroduction,
    PaymentDialog,
    Error404,
    AdminModal: modals.AdminModal,
    MuteModal: modals.MuteModal,
    ReportModal: modals.ReportModal,
    WarningModal: modals.WarningModal,
    GiftEffect,
  },

  setup() {
    const commentContainerStyle = ref({});
    const roomContentEl = ref<HTMLElement>();

    const store = useStore();
    const route = useRoute();
    const hostingEnv = useHostingEnv();
    const roomId = Number(route.params.roomId || 0);

    const roomInfo = computed(() => store.state.room.roomInfo);
    const isRoomError = computed(() => roomInfo.value === DATA_ERROR);

    if (!roomId) {
      store.commit('setRoomInfo', DATA_ERROR);
      return {
        roomId,
        roomInfo,
        isRoomError,
      };
    }

    useServerPrefetch(async() => await store.dispatch('getRoomInfo', { roomId }), store.state.room.roomInfo);

    const isBrowser = computed(() => hostingEnv.isBrowser);

    const loginContext = useLogin();

    const showGifts = computed(() => {
      return roomInfo?.value?.rights?.no_treasure !== 1;
    });

    const showRank = computed(() => {
      return roomInfo?.value?.rights?.no_rank !== 1;
    });

    const onLogin = () => {
      loginContext.openLogin();
    };

    onMounted(async () => {
      if (!roomId) {
        isRoomError.value = true;
        return;
      }

      try {
        const data = await store.dispatch('getRoomInfo', { roomId });
        
        // 开启长链
        initBSocket(roomId, !!data?.block_info);

        emitter.on(EB_EVENT_NAME.LIVE_CLOSE, () => store.dispatch('getRoomInfo', { roomId }));
        emitter.on(EB_EVENT_NAME.LIVE_OPEN, () => store.dispatch('getRoomInfo', { roomId }));

        emitter.on(EB_EVENT_NAME.LIVE_BLOCK, params => {
          store.dispatch('blockLive', params);
        });
        emitter.on(EB_EVENT_NAME.NEED_LOGIN, onLogin);

        const changeHeight = () => {
          const rankHeight = showRank.value ? 192 : 0;
          const giftControlHeight = showGifts.value ? 108 : 0;
          const roomInfoHeight = 96;
          const roomVideoHeight = roomContentEl.value?.offsetWidth * (9 / 16);
          // room-video-height + room-info-height + gift-control-height  - rankHeight
          const height = roomVideoHeight + roomInfoHeight + giftControlHeight - rankHeight + 'px';

          commentContainerStyle.value = { height };
        };

        window.addEventListener('resize', changeHeight);
        await nextTick();
        changeHeight();
      } catch (e) {
        console.log('[isRoomError]:', e);
        isRoomError.value = true;
      }
    });

    onBeforeUnmount(() => {
      closeBSocket();
    });

    return {
      roomId,
      roomInfo,
      isRoomError,
      roomContentEl,
      showGifts,
      showRank,
      onLogin,
      commentContainerStyle,
      isBrowser,
      DATA_ERROR,
    };
  },
});
</script>

<style lang="less" scoped>
@import '@/styles/var.less';

@media screen and (min-width: 1580px) {
  .room {
    width: 1580px;
  }

  .room-content {
    width: 1188px;
  }
}

@media screen and (min-width: 1068px) and (max-width: 1580px) {
  .room {
    width: 100%;
  }

  .room-content {
    width: calc(100% - 372px);
  }
}

@media screen and (max-width: 1068px) {
  .room {
    width: 1068px;
  }

  .room-content {
    width: 676px;
  }
}

.room-page {
  background: #f9f9f9;
  min-height: 100vh;

  .room {
    padding: 24px 12px;
    max-width: 1580px;
    min-width: 1068px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
  }

  .room-content {
    max-width: 1188px;
    min-width: 676px;

    .room-video-container {
      background: #000;
    }
  }

  .gift-control-container {
    position: relative;
    height: 108px;
  }

  .room-bot-banner {
    width: 100%;
    height: 120px;
    margin-top: 25px;
    overflow: hidden;
    position: relative;
    border-radius: 8px;

    .room-bot-banner-img {
      display: block;
      border-radius: 8px;
      height: 120px;
      max-width: 1352px;
      margin: 0 auto;
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .comment-area {
    display: flex;
    flex-direction: column;
    width: 351px;
    margin-left: 16px;
    position: relative;

    .live-rank-container {
      flex: 0 0 192px;
      background: #fff;
      border-radius: 8px 8px 0 0;
      position: relative;
      z-index: @rightBar_layer_index_rankList;
      border: 1px solid rgba(238, 238, 238, 1);
      border-bottom: none;
    }

    .live-comment-container {
      flex-grow: 1;
      background: #fafafa;
      border-radius: 0 0 8px 8px;
      position: relative;
      border: 1px solid rgba(238, 238, 238, 1);
      z-index: @rightBar_layer_index_comment;
    }

    .comment-area-loading {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      &__img {
        height: 80px;
        display: block;
        margin: 0 auto;
      }

      &__txt {
        font-size: 12px;
        color: #999;
        text-align: center;
        line-height: 14px;
        margin: 8px 0 0;
      }
    }
  }

  .live-introduction-container {
    height: 120px;
    margin-top: 25px;
    position: relative;
  }

  .gift-effect-container {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 234px;
    width: 100%;
    background: transparent;
    z-index: @rightBar_layer_index_animation_effect;
    pointer-events: none;
    // max-height: calc(100% - 87px);
  }
}
</style>

<style>
body {
  user-select: none;
}
</style>